import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { useGetAvailableRewardsQuery } from 'api/recoveryAi'
import type { RecoveryConfig } from 'api/recoveryAi.types'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ActiveLocationsConfigRow from './ActiveLocationsConfigRow'
import ApprovedRewardsBody from './ApprovedRewardsBody'
import BrandVoiceModal from './BrandVoiceModal'
import { DraftRowKey, isDraftRowKey, RowKey } from './configRowKeys'
import ConfigRowModal from './ConfigRowModal'
import EscalationContactsConfigRow from './EscalationContactsConfigRow'
import EscalationRulesModalBody from './EscalationRulesModalBody'
import GenerositySliderBody from './GenerositySliderBody'
import {
  RecoveryConfigDraftProvider,
  useRecoveryConfigDraft,
} from './RecoveryConfigDraftContext'
import { DEFAULT_RECOMMENDED_POSTURE } from './stances'
import { successNotificationOverrides } from './successNotificationOverrides'
import { MakeGoodCuration, useMakeGoodCuration } from './useMakeGoodCuration'

const tTabs = buildTranslate('recovery_ai.settings.tabs')
const tModal = buildTranslate('recovery_ai.config_modal')
const tApproved = buildTranslate('recovery_ai.settings.approved_rewards')
// Intro copy for the modal bodies — the SAME keys the Settings panes render, so
// each modal and its pane sibling read identically (D6 parity).
const tPhilosophy = buildTranslate('recovery_ai.wizard.philosophy')

// Post-save banner copy. Two independent axes — "Save and test" vs plain Save, and
// whether this surface also restarted its simulator conversation — so it is a function
// with early returns rather than a nested ternary. A host that never restarts (the
// Settings panes, the activation summary) only ever reaches the two original keys.
const savedBannerKey = (viaTest: boolean, restarted: boolean): string => {
  if (viaTest) {
    return restarted ? 'save_success_test_restarted' : 'save_success_test'
  }
  return restarted ? 'save_success_restarted' : 'save_success'
}

type RewardsQuery = ReturnType<typeof useGetAvailableRewardsQuery>

/**
 * Controlled-or-self-managed open-row state, shared by the registry and by the
 * trigger chrome that drives it (the Agent-Tuning chevron rows, the activation
 * summary's Change buttons). The chrome resolves the pair once and hands the
 * registry a controlled pair, so there is exactly one source of truth.
 *
 * context: `openRow` is the controlled *value*, `onOpenRow` its *setter*. Control
 * mode keys on the setter, so a caller that passes the value and forgets the
 * setter silently falls into self-managed mode with its `openRow` ignored — a
 * dead prop with no warning. Warn once in dev so the misuse shows up at the call
 * site rather than as a row that will not open.
 */
export const useOpenRowState = (
  componentName: string,
  controlledOpenRow?: RowKey | null,
  onOpenRow?: (key: RowKey | null) => void
): { openRow: RowKey | null; setOpenRow: (key: RowKey | null) => void } => {
  const [internalOpenRow, setInternalOpenRow] = useState<RowKey | null>(null)
  const isControlled = onOpenRow !== undefined

  const hasWarnedControlledMisuseRef = useRef(false)
  if (
    process.env.NODE_ENV !== 'production' &&
    !hasWarnedControlledMisuseRef.current &&
    controlledOpenRow !== undefined &&
    onOpenRow === undefined
  ) {
    hasWarnedControlledMisuseRef.current = true
    // eslint-disable-next-line no-console
    console.error(
      `[${componentName}] \`openRow\` was provided without \`onOpenRow\`. ` +
        'The component is controlled only when `onOpenRow` is supplied, so this ' +
        '`openRow` value is ignored and the component stays uncontrolled. Pass ' +
        'both props for controlled mode, or neither for uncontrolled.'
    )
  }

  const openRow = isControlled ? controlledOpenRow ?? null : internalOpenRow
  const setOpenRow = useCallback(
    (key: RowKey | null) => {
      if (onOpenRow) onOpenRow(key)
      else setInternalOpenRow(key)
    },
    [onOpenRow]
  )

  return { openRow, setOpenRow }
}

type DraftConfigRowProps = {
  rowKey: DraftRowKey
  rewardsQuery: RewardsQuery
  // Make-good curation (AI-330), threaded from the registry so the modal's
  // Approved-rewards body is NON-read-only — it gets the same ladder "×" as the
  // Settings pane and the wizard. Decline and Regenerate are immediate writes,
  // independent of the modal's staged draft commit.
  makeGood: MakeGoodCuration
  onClose: () => void
  onSaved: () => void
  onSavedAndTest: () => void
  hideSaveAndTest?: boolean
  /** Host width override, forwarded to the shared shell. */
  width?: string
  /** Host Save-button copy override, forwarded to the shared shell. */
  saveLabel?: string
}

// The three rows that persist through RecoveryConfigDraftContext. One component
// so the `useRecoveryConfigDraft()` call is unconditional, and one place where
// the draft value is handed to ConfigRowModal as its save adapter — it satisfies
// ConfigRowSaveAdapter structurally, with no mapper (DRAFT_SATISFIES_ADAPTER).
// Must be a child of the provider.
const DraftConfigRow = ({
  rowKey,
  rewardsQuery,
  makeGood,
  onClose,
  onSaved,
  onSavedAndTest,
  hideSaveAndTest = false,
  width,
  saveLabel,
}: DraftConfigRowProps) => {
  const draft = useRecoveryConfigDraft()
  const { setField } = draft
  // The draft context names its load fields isConfigLoading / isConfigError;
  // renaming them into the save adapter would break its zero-shim property, so
  // they arrive as a separate two-line load state (L37).
  const load = {
    isLoading: draft.isConfigLoading,
    isLoadError: draft.isConfigError,
  }

  const {
    data: availableRewards,
    isLoading: rewardsLoading,
    isError: rewardsError,
  } = rewardsQuery
  const activeRewards = useMemo(
    () => (availableRewards ?? []).filter(r => r.active),
    [availableRewards]
  )
  const activeRewardIds = useMemo(
    () => new Set(activeRewards.map(r => r.id)),
    [activeRewards]
  )

  let body: React.ReactNode = null
  if (rowKey === 'recovery_generosity') {
    body = (
      <>
        {/* D6 parity: the Settings pane (RecoveryGenerosityPane) leads with this
            description and so does the wizard Figma modal — mirror it here above
            the slider. The pane's onboarding-only "Recommendation is based on your
            assessment" line stays omitted (no live assessment post-onboarding). */}
        <div className="mb-l">
          <Text.BodyText3 color="grey70">
            {tPhilosophy('description')}
          </Text.BodyText3>
        </div>
        <GenerositySliderBody
          value={draft.draft.philosophy_posture ?? DEFAULT_RECOMMENDED_POSTURE}
          onSelect={posture => setField('philosophy_posture', posture)}
          recommendedPosture={
            draft.draft.philosophy_recommended_posture ??
            DEFAULT_RECOMMENDED_POSTURE
          }
        />
      </>
    )
  } else if (rowKey === 'escalation_rules') {
    // AI-64: the escalation rules body stages through the same draft; Save
    // commits one PUT, Cancel rolls back (shared EscalationRulesCard, no fork).
    body = <EscalationRulesModalBody />
  } else if (rewardsLoading) {
    // Remaining branches are the 'approved_rewards' row. Guard the rewards GET:
    // never present the empty picker as "no rewards" while the catalog is still
    // loading or after it failed (mirrors the pane).
    body = (
      <div role="status" aria-label={tApproved('loading')}>
        <Spinner isLoading size="4x" className="text-center" />
      </div>
    )
  } else if (rewardsError) {
    body = (
      <Notification kind="negative" size="full">
        {tApproved('fetch_error')}
      </Notification>
    )
  } else {
    body = (
      <>
        {/* D6 parity: the Settings pane (ApprovedRewardsPane) leads with this
            description, and so does the Figma modal — mirror it above the body. */}
        <div className="mb-l">
          <Text.BodyText3 color="grey70">
            {tApproved('description')}
          </Text.BodyText3>
        </div>
        <ApprovedRewardsBody
          rewards={activeRewards}
          // Active-filter the seeded value so the body (and therefore every staged
          // edit + the committed payload) can never carry a since-deactivated id —
          // the defense-in-depth the pane's buildPayload applies.
          value={(draft.draft.approved_reward_ids ?? []).filter(id =>
            activeRewardIds.has(id)
          )}
          onChange={ids => setField('approved_reward_ids', ids)}
          makeGoodOffers={makeGood.offers}
          onDeclineMakeGood={makeGood.onDecline}
          isDecliningMakeGood={makeGood.isDeclining}
          onRegenerateMakeGood={makeGood.onRegenerate}
          isRegeneratingMakeGood={makeGood.isRegenerating}
          makeGoodOffersLoaded={makeGood.offersLoaded}
          makeGoodRegenerateErrorKind={makeGood.regenerateErrorKind}
          makeGoodDeclineError={makeGood.declineError}
        />
      </>
    )
  }

  return (
    <ConfigRowModal
      rowKey={rowKey}
      adapter={draft}
      load={load}
      onClose={onClose}
      onSaved={onSaved}
      onSaveAndTest={hideSaveAndTest ? undefined : onSavedAndTest}
      // L3: a regenerate is non-save work that must still block dismiss (and
      // Save-and-test) — close() resets the mutation, so dismissing mid-flight
      // would discard the outcome the merchant asked for. Plain Save is NOT
      // blocked: the config PUT and the regenerate mutation touch disjoint cache
      // tags (RecoveryConfig vs RecoveryMakeGoodLadder — CONTRACT.md §5.6), so
      // committing a staged edit mid-regenerate is safe, and blocking it would
      // just make the merchant wait on an unrelated write (see ConfigModalHost's
      // isBusy doc). Passed for every draft row rather than only
      // approved_rewards: the flag can only be raised from the rewards ladder,
      // and the modal that raised it is the one dismiss blocks.
      isBusy={makeGood.isRegenerating}
      width={width}
      saveLabel={saveLabel}
    >
      {body}
    </ConfigRowModal>
  )
}

type Props = {
  /** Which rows this host may open. Also drives `skip` on the row-specific
   *  queries, so a host that can never open approved_rewards never fetches the
   *  reward catalog. */
  rows: RowKey[]
  openRow?: RowKey | null
  onOpenRow?: (key: RowKey | null) => void
  hideSaveAndTest?: boolean
  onTest?: () => void
  onCommitted?: (staged: Partial<RecoveryConfig>) => void
  /** Namespaces the ids the adapter rows' pickers derive, so two surfaces can
   *  co-mount without colliding aria-describedby targets. Consumed by the
   *  active-locations and escalation-contacts rows. */
  instanceId: string
  /**
   * Per-row dialog widths for THIS host, overriding the row defaults.
   *
   *   context: 600px is a Settings-column width, and the activation summary's
   *   Figma is far wider. Passing the map per host rather than widening
   *   ROW_MODAL_WIDTH keeps the Agent Tuning tab and the wizard's Testing stage on
   *   their shipped widths — the same rows render there, and nothing has asked for
   *   those to change. See ACTIVATION_ROW_MODAL_WIDTH.
   */
  rowWidths?: Partial<Record<RowKey, string>>
  /**
   * Per-row Save-button copy for THIS host, overriding the default "Save".
   * The Agent-Tuning host passes "Save changes" for approved_rewards (Figma
   * parity); every other host/row omits it and keeps ConfigModalHost's default.
   */
  rowSaveLabels?: Partial<Record<RowKey, string>>
  /**
   * Whether a successful save also reloads the host's simulator conversation, so the
   * post-save banner can say so. Default false, which keeps every host that has no
   * simulator (the Settings panes, the activation summary) on the original copy.
   *
   * A prop rather than something inferred, because it varies WITHIN a host: the wizard
   * passes `isFree`, since its guided phase deliberately does not restart.
   *
   *   context: turning this on makes the banner promise a restart, which carries the
   *   nexus #1378 backend dependency — see usePendingConversationReset's docstring for
   *   what the pre-deploy failure looks like. Do not opt a host in without it.
   */
  conversationRestartsOnSave?: boolean
}

// A RowKey that reaches the end of renderOpenRow's chain has no renderer, which
// `never` turns into a build failure at the call site. Unused param by design —
// the type is the assertion.
const assertNoRenderer = (_key: never): null => null

// THE config-modal registry: one prop-driven host for every RecoveryAI config
// row, so the Agent Tuning tab and the activation summary open the same dialogs
// from the same code rather than growing a second, drifting copy (L19).
//
// context: the trigger chrome (chevron rows, Change buttons) stays with each
// host — it owns the open-row state via useOpenRowState and hands this component
// a controlled pair. What lives here is everything downstream of "a row is open":
// the draft provider, the row switch, the post-save banner, and the save
// callbacks. The provider mounts ONLY while a row is open, so each open seeds a
// clean draft from the saved config and no host pays for the modal tree when it
// is closed.
const RecoveryConfigModals = ({
  rows,
  openRow: controlledOpenRow,
  onOpenRow,
  hideSaveAndTest = false,
  onTest,
  onCommitted,
  instanceId,
  rowWidths,
  rowSaveLabels,
  conversationRestartsOnSave = false,
}: Props) => {
  const { openRow, setOpenRow } = useOpenRowState(
    'RecoveryConfigModals',
    controlledOpenRow,
    onOpenRow
  )
  // A row this host was not given cannot open, however it was asked.
  const activeRow = openRow && rows.includes(openRow) ? openRow : null

  // The row whose save just succeeded — drives the post-close "Saved" banner
  // (the modal is gone on success, so the confirmation lives on the page the
  // merchant returns to). Cleared when another row opens.
  const [savedRow, setSavedRow] = useState<RowKey | null>(null)
  // Whether that save was via "Save and test" — selects the banner copy.
  const [savedViaTest, setSavedViaTest] = useState(false)
  // Whether the save that produced the banner actually PUT something. Needed because
  // "Save and test" is NOT isDirty-gated (canSaveAndTest only checks the config load),
  // so a clean draft can reach handleSavedAndTest with no commit behind it — and the
  // restart copy would then claim a restart that never happened.
  //   context: ordering is reliable. commit() calls onCommitted?.(staged) synchronously
  //   before returning true, and onSave/onSaveAndTest only call onSaved/onSavedAndTest
  //   after that resolves.
  const didCommitRef = useRef(false)
  // Whether the banner should mention the conversation restart, frozen at save time so
  // a later prop change cannot retroactively rewrite it.
  const [savedRestarted, setSavedRestarted] = useState(false)

  // Only the approved-rewards row reads the catalog, and only while it is open.
  // A host that prefetches it for its own row labels (the Agent Tuning tab) has
  // already warmed the cache, so this resolves without a spinner on first open.
  const rewardsQuery = useGetAvailableRewardsQuery(undefined, {
    skip: activeRow !== 'approved_rewards',
  })

  // Make-good curation (AI-330/AI-383), unconditional + fail-soft — shared with
  // the Settings pane so the two curation surfaces cannot drift. Skipped unless
  // the ladder is on screen: this registry outlives every modal, and only the
  // approved-rewards body renders the offers, so a closed host would otherwise
  // hold a live GET for a surface no one is looking at.
  const makeGood = useMakeGoodCuration({
    skip: activeRow !== 'approved_rewards',
  })
  // This registry outlives the modal while the ladder unmounts on close, so the
  // regenerate mutation's error state would otherwise replay on the next open
  // with no attempt behind it.
  const clearRegenerateFeedback = makeGood.clearFeedback

  // A fresh open supersedes a prior "Saved" confirmation. Guarded on non-null so
  // the close after a successful save (openRow -> null) does not wipe the
  // just-set banner.
  //
  // context: regenerate feedback is NOT cleared here. Every path out of an open
  // modal (close / handleSaved / handleSavedAndTest) already clears it, so it is
  // provably clear by the time a row can reopen — and clearing on the openRow
  // edge would also fire when `reset`'s identity churns mid-open, wiping the
  // inline regenerate error the merchant is meant to read.
  useEffect(() => {
    if (openRow) setSavedRow(null)
  }, [openRow])

  const close = useCallback(() => {
    clearRegenerateFeedback()
    setOpenRow(null)
  }, [setOpenRow, clearRegenerateFeedback])

  // The provider fires this ONLY after a successful PUT — its `!isDirty` early return
  // resolves true without it — so it is the one signal that means "config changed".
  const handleCommitted = useCallback(
    (staged: Partial<RecoveryConfig>) => {
      didCommitRef.current = true
      onCommitted?.(staged)
    },
    [onCommitted]
  )

  const handleSaved = useCallback(() => {
    setSavedViaTest(false)
    setSavedRestarted(didCommitRef.current && conversationRestartsOnSave)
    didCommitRef.current = false
    setSavedRow(openRow)
    clearRegenerateFeedback()
    setOpenRow(null)
  }, [openRow, setOpenRow, clearRegenerateFeedback, conversationRestartsOnSave])

  const handleSavedAndTest = useCallback(() => {
    setSavedViaTest(true)
    setSavedRestarted(didCommitRef.current && conversationRestartsOnSave)
    didCommitRef.current = false
    setSavedRow(openRow)
    clearRegenerateFeedback()
    setOpenRow(null)
    // Scroll the on-tab simulator into view (the host owns the ref).
    onTest?.()
  }, [
    openRow,
    onTest,
    setOpenRow,
    clearRegenerateFeedback,
    conversationRestartsOnSave,
  ])

  const renderOpenRow = (key: RowKey) => {
    // Brand voice owns its own two-view ConfigModalHost (custom footer + "← Back"
    // upload sub-view), so it short-circuits the shared shell. It still consumes
    // the provider, so it stages and commits identically.
    if (key === 'brand_voice') {
      return <BrandVoiceModal onClose={close} onSaved={handleSaved} />
    }
    if (isDraftRowKey(key)) {
      return (
        <DraftConfigRow
          rowKey={key}
          rewardsQuery={rewardsQuery}
          makeGood={makeGood}
          onClose={close}
          onSaved={handleSaved}
          onSavedAndTest={handleSavedAndTest}
          hideSaveAndTest={hideSaveAndTest}
          width={rowWidths?.[key]}
          saveLabel={rowSaveLabels?.[key]}
        />
      )
    }
    // The adapter rows each own a save controller, so each arrives as its own
    // component — their hooks cannot be called conditionally from here, and
    // their endpoint hooks must stay out of the Agent-Tuning render tree (whose
    // suites mock api/recoveryAi with literal factories).
    if (key === 'active_locations') {
      return (
        <ActiveLocationsConfigRow
          instanceId={instanceId}
          onClose={close}
          onSaved={handleSaved}
          width={rowWidths?.active_locations}
        />
      )
    }
    if (key === 'escalation_contacts') {
      return (
        <EscalationContactsConfigRow
          instanceId={instanceId}
          onClose={close}
          onSaved={handleSaved}
          width={rowWidths?.escalation_contacts}
        />
      )
    }
    // Every branch above narrows, so a RowKey with no renderer reaches here as a
    // COMPILE error. It used to fall through to escalation contacts — a fully
    // editable recipients form under the new row's title, which is worse than the
    // dead button the CARD side is guarded against. Returns null rather than
    // throwing so a bypass degrades to "nothing opens" instead of a blank page.
    return assertNoRenderer(key)
  }

  return (
    <>
      {/* Post-save confirmation — POLITE so it announces without interrupting
          (shared override). Shows after the modal closes on a successful Save;
          reuses the per-setting copy the Settings panes use. */}
      {savedRow && (
        <div className="mb-m">
          <Notification
            kind="positive"
            size="full"
            overrides={successNotificationOverrides}
          >
            {tModal(savedBannerKey(savedViaTest, savedRestarted), {
              setting: tTabs(savedRow),
            })}
          </Notification>
        </div>
      )}

      {activeRow && (
        <RecoveryConfigDraftProvider onCommitted={handleCommitted}>
          {renderOpenRow(activeRow)}
        </RecoveryConfigDraftProvider>
      )}
    </>
  )
}

export default RecoveryConfigModals
